/** Traffic-light evaluation for the device Hardware tab. */

import { formatRam } from '@/lib/formatAgent'
import { isExcluded, normaliseExclusions } from '@/lib/postureExclusions'
import { DEFAULT_POSTURE_THRESHOLDS } from '@/lib/postureThresholds'
import { buildPosture, mapSectionResults, worstStatus } from '@/lib/trafficLight'

export { TRAFFIC } from '@/lib/trafficLight'

const HARDWARE_SECTIONS = [
  { id: 'memory', title: 'Memory' },
  { id: 'processor', title: 'Processor' },
  { id: 'storage', title: 'Storage' },
  { id: 'firmware', title: 'Firmware' },
]

const HEADLINES = {
  green: 'Hardware meets typical standards for this device type',
  amber: 'Review recommended — some specs are below par',
  red: 'Action needed — hardware may be limiting performance',
  grey: 'Not enough hardware data to assess this device',
}

/** @deprecated Prefer thresholds from Admin → Posture thresholds. */
export const RAM_EIGHT_GB_FLOOR_MB = DEFAULT_POSTURE_THRESHOLDS.workstation_ram_amber_below_mb
/** @deprecated Prefer thresholds from Admin → Posture thresholds. */
export const DISK_LOW_PCT = DEFAULT_POSTURE_THRESHOLDS.disk_low_pct
/** @deprecated Prefer thresholds from Admin → Posture thresholds. */
export const DISK_LOW_GB = DEFAULT_POSTURE_THRESHOLDS.disk_low_gb
/** @deprecated Prefer thresholds from Admin → Posture thresholds. */
export const DISK_CRITICAL_PCT = DEFAULT_POSTURE_THRESHOLDS.disk_critical_pct
/** @deprecated Prefer thresholds from Admin → Posture thresholds. */
export const DISK_CRITICAL_GB = DEFAULT_POSTURE_THRESHOLDS.disk_critical_gb

function resolveThresholds(thresholds) {
  return { ...DEFAULT_POSTURE_THRESHOLDS, ...(thresholds || {}) }
}

function ramThresholds(machineType, thresholds) {
  const t = resolveThresholds(thresholds)
  const isServer = machineType === 'server'
  return {
    redBelowMb: isServer ? t.server_ram_red_below_mb : t.workstation_ram_red_below_mb,
    amberBelowMb: isServer ? t.server_ram_amber_below_mb : t.workstation_ram_amber_below_mb,
  }
}

function asObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value
}

function diskMetrics(hardware) {
  const rows = hardware.filter((row) => row.category === 'disk')
  let lowestFreePct = null
  let lowestFreeGb = null
  let smallestTotalMb = null

  for (const row of rows) {
    const details = asObject(row.details)
    if (!details || details.Total == null) continue
    const total = Number(details.Total)
    const free = Number(details.Free)
    if (Number.isNaN(total) || total <= 0) continue

    if (smallestTotalMb == null || total < smallestTotalMb) {
      smallestTotalMb = total
    }
    if (!Number.isNaN(free)) {
      const pct = (free / total) * 100
      const freeGb = free / 1024
      if (lowestFreePct == null || pct < lowestFreePct) {
        lowestFreePct = pct
      }
      if (lowestFreeGb == null || freeGb < lowestFreeGb) {
        lowestFreeGb = freeGb
      }
    }
  }

  return { lowestFreePct, lowestFreeGb, smallestTotalMb, diskCount: rows.length }
}

/** Prefer RMM disk-space snapshot volumes; fall back to inventory metrics. */
export function resolveDiskFree(diskSnapshot, inventoryMetrics = {}) {
  const volumes = Array.isArray(diskSnapshot?.volumes) ? diskSnapshot.volumes : []
  let lowestFreePct =
    diskSnapshot?.lowest_free_percent != null ? Number(diskSnapshot.lowest_free_percent) : null
  let lowestFreeGb = null

  for (const vol of volumes) {
    const pct = vol.free_percent != null ? Number(vol.free_percent) : null
    const gb = vol.free_gb != null ? Number(vol.free_gb) : null
    if (pct != null && !Number.isNaN(pct) && (lowestFreePct == null || pct < lowestFreePct)) {
      lowestFreePct = pct
    }
    if (gb != null && !Number.isNaN(gb) && (lowestFreeGb == null || gb < lowestFreeGb)) {
      lowestFreeGb = gb
    }
  }

  if (lowestFreeGb == null && diskSnapshot?.system_free_gb != null) {
    const systemGb = Number(diskSnapshot.system_free_gb)
    if (!Number.isNaN(systemGb)) lowestFreeGb = systemGb
  }

  if (lowestFreePct == null || Number.isNaN(lowestFreePct)) {
    lowestFreePct = inventoryMetrics.lowestFreePct ?? null
  }
  if (lowestFreeGb == null || Number.isNaN(lowestFreeGb)) {
    lowestFreeGb = inventoryMetrics.lowestFreeGb ?? null
  }

  return { lowestFreePct, lowestFreeGb }
}

export function diskSpaceStatus(lowestFreePct, lowestFreeGb, thresholds) {
  const t = resolveThresholds(thresholds)
  const pct = lowestFreePct != null && !Number.isNaN(Number(lowestFreePct))
    ? Number(lowestFreePct)
    : null
  const gb = lowestFreeGb != null && !Number.isNaN(Number(lowestFreeGb))
    ? Number(lowestFreeGb)
    : null

  if (pct == null && gb == null) return null

  const critical =
    (pct != null && pct < t.disk_critical_pct) || (gb != null && gb < t.disk_critical_gb)
  if (critical) return 'red'

  const low = (pct != null && pct < t.disk_low_pct) || (gb != null && gb < t.disk_low_gb)
  if (low) return 'amber'

  return 'green'
}

function diskIssueLabel(lowestFreePct, lowestFreeGb) {
  const parts = []
  if (lowestFreePct != null && !Number.isNaN(lowestFreePct)) {
    parts.push(`${Math.round(lowestFreePct)}% free`)
  }
  if (lowestFreeGb != null && !Number.isNaN(lowestFreeGb)) {
    parts.push(`${Number(lowestFreeGb).toFixed(1)} GB free`)
  }
  return parts.join(', ') || 'unknown free space'
}

function biosReleaseDate(hardware) {
  const row = hardware.find((item) => item.name === 'BIOS')
  const date = asObject(row?.details)?.BiosReleaseDate
  if (!date) return null
  const parsed = new Date(date)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function evaluateMemory({ agent, thresholds, exclusions }) {
  const issues = []
  let status = 'grey'
  if (isExcluded(exclusions, 'ram')) {
    return { id: 'memory', status, issues }
  }
  const ramMb = agent?.ram_mb

  if (ramMb != null && ramMb > 0) {
    status = 'green'
    const { redBelowMb, amberBelowMb } = ramThresholds(agent.machine_type, thresholds)
    if (ramMb < redBelowMb) {
      issues.push(`Low RAM (${formatRam(ramMb)})`)
      status = 'red'
    } else if (ramMb < amberBelowMb) {
      issues.push(`RAM below recommended (${formatRam(ramMb)})`)
      status = 'amber'
    }
  }

  return { id: 'memory', status, issues }
}

function evaluateProcessor({ agent, thresholds, exclusions }) {
  const t = resolveThresholds(thresholds)
  const issues = []
  let status = 'grey'
  if (isExcluded(exclusions, 'processor')) {
    return { id: 'processor', status, issues }
  }
  const cores = agent?.cpu_cores
  const speedMhz = agent?.cpu_speed_mhz

  if (cores != null || speedMhz != null || agent?.cpu_name) {
    status = 'green'
  }

  // CPU is always Review (amber) at most — never Action needed.
  if (cores != null) {
    if (cores < t.cpu_cores_red_below) {
      issues.push(`Only ${cores} CPU core(s)`)
      status = worstStatus(status, 'amber')
    } else if (cores < t.cpu_cores_amber_below) {
      issues.push(`Low core count (${cores})`)
      status = worstStatus(status, 'amber')
    }
  }

  if (speedMhz != null && speedMhz > 0 && speedMhz < t.cpu_speed_amber_below_mhz) {
    issues.push(`Slow CPU (${(speedMhz / 1000).toFixed(2)} GHz)`)
    status = worstStatus(status, 'amber')
  }

  return { id: 'processor', status, issues }
}

function evaluateStorage({ hardware, diskSnapshot, thresholds, exclusions }) {
  const t = resolveThresholds(thresholds)
  const issues = []
  let status = 'grey'
  const fromInventory = diskMetrics(hardware)
  const { lowestFreePct, lowestFreeGb } = resolveDiskFree(diskSnapshot, fromInventory)
  const spaceStatus = isExcluded(exclusions, 'disk_free')
    ? null
    : diskSpaceStatus(lowestFreePct, lowestFreeGb, t)

  if (spaceStatus) {
    status = spaceStatus
    if (spaceStatus === 'red') {
      issues.push(`Critically low disk space (${diskIssueLabel(lowestFreePct, lowestFreeGb)})`)
    } else if (spaceStatus === 'amber') {
      issues.push(`Low disk space (${diskIssueLabel(lowestFreePct, lowestFreeGb)})`)
    }
  } else if (fromInventory.diskCount > 0 || Object.keys(diskSnapshot || {}).length > 0) {
    status = 'green'
  }

  if (!isExcluded(exclusions, 'small_drive')) {
    const smallestMb = fromInventory.smallestTotalMb
    const smallDriveMb = Number(t.small_drive_below_gb) * 1024
    if (smallestMb != null && smallestMb < smallDriveMb) {
      issues.push(`Small drive detected (${formatRam(smallestMb)} total)`)
      status = worstStatus(status, 'amber')
    }
  }

  return { id: 'storage', status, issues }
}

function evaluateFirmware({ hardware, thresholds, exclusions }) {
  const t = resolveThresholds(thresholds)
  const issues = []
  let status = 'grey'
  if (isExcluded(exclusions, 'bios_age')) {
    return { id: 'firmware', status, issues }
  }
  const biosDate = biosReleaseDate(hardware)

  if (biosDate) {
    status = 'green'
    const ageYears = (Date.now() - biosDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
    if (ageYears >= t.bios_age_amber_years) {
      issues.push(`BIOS is ${Math.floor(ageYears)} years old — consider updating`)
      status = 'amber'
    }
  }

  return { id: 'firmware', status, issues }
}

export function evaluateHardwarePosture({
  agent,
  hardware = [],
  snapshots = [],
  thresholds = null,
  exclusions = null,
}) {
  const t = resolveThresholds(thresholds)
  const excl = normaliseExclusions(exclusions ?? agent?.posture_exclusions)
  const diskSnapshot = snapshots.find((s) => s.script_slug === 'disk-space')?.payload ?? {}

  const evaluators = [
    evaluateMemory({ agent, thresholds: t, exclusions: excl }),
    evaluateProcessor({ agent, thresholds: t, exclusions: excl }),
    evaluateStorage({ hardware, diskSnapshot, thresholds: t, exclusions: excl }),
    evaluateFirmware({ hardware, thresholds: t, exclusions: excl }),
  ]

  const sections = mapSectionResults(HARDWARE_SECTIONS, evaluators)
  return buildPosture({ sections, sectionDefs: HARDWARE_SECTIONS, headlines: HEADLINES })
}
